import { useState, useEffect } from "react";
import { T } from "../data/Tokens";
import { apiFetch } from "../../api/api";
import {
  Card, PageHeader, GhostBtn, StatusPill,
  SearchBar, Avatar, Th, Td, Tr, Tabs, StatCard, EmptyState, ConfirmModal
} from "../components/common/Ui";

export default function InquiriesAdmin() {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats]         = useState({ New: 0, Contacted: 0, Resolved: 0 });
  const [tab, setTab]             = useState("all");
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [detail, setDetail]       = useState(null);
  const [delId, setDelId]         = useState(null);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, color = T.gold) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/inquiries/list.php");
      setInquiries(res.data);
      setStats(res.stats);
    } catch (err) {
      setError(err.message || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await apiFetch("/inquiries/update.php", { method: "PUT", body: { id, status } });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      setStats(prev => {
        const copy = { ...prev };
        const old = inquiries.find(i => i.id === id)?.status;
        if (old && copy[old] > 0) copy[old]--;
        copy[status] = (copy[status] || 0) + 1;
        return copy;
      });
      if (detail?.id === id) setDetail(d => ({ ...d, status }));
      showToast(`✓ Marked as ${status}`, T.success);
    } catch (err) {
      showToast(err.message || "Update failed", T.danger);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch("/inquiries/delete.php", { method: "DELETE", body: { id } });
      setInquiries(prev => prev.filter(i => i.id !== id));
      showToast("🗑 Inquiry deleted", T.danger);
    } catch (err) {
      showToast(err.message || "Delete failed", T.danger);
    } finally {
      setDelId(null);
      setDetail(null);
    }
  };

  const STATS = [
    { icon:"📥", label:"Total Inquiries", value: inquiries.length, sub:"All time",         color: T.gold },
    { icon:"🆕", label:"New",             value: stats.New || 0,   sub:"Needs response",   color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"📞", label:"Contacted",       value: stats.Contacted || 0, sub:"In progress",  color: T.warning, bg:"#FFF8EC" },
    { icon:"✅", label:"Resolved",        value: stats.Resolved || 0,  sub:"Closed",       color: T.success },
  ];

  const TABS = [
    { id:"all",       label:"All",       count: inquiries.length },
    { id:"New",       label:"New",       count: stats.New || 0 },
    { id:"Contacted", label:"Contacted", count: stats.Contacted || 0 },
    { id:"Resolved",  label:"Resolved",  count: stats.Resolved || 0 },
  ];

  const filtered = inquiries.filter(i => {
    const mt = tab === "all" || i.status === tab;
    const q = search.toLowerCase();
    const ms = !search ||
      `${i.first_name} ${i.last_name}`.toLowerCase().includes(q) ||
      (i.email || "").toLowerCase().includes(q) ||
      (i.company || "").toLowerCase().includes(q);
    return mt && ms;
  });

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); }
    catch { return d; }
  };

  return (
    <div>
      <PageHeader title="Inquiries" subtitle={`${inquiries.length} corporate inquiries from your website`}>
        <GhostBtn onClick={load}>↻ Refresh</GhostBtn>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
        {STATS.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <Card>
        <div className="px-4 sm:px-5 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search by name, email or company..." />
        </div>

        {loading ? (
          <div className="py-16 text-center text-[13px]" style={{ color:T.textSec }}>Loading inquiries…</div>
        ) : error ? (
          <div className="py-16 text-center px-4">
            <p className="text-[13px] mb-3" style={{ color:T.danger }}>{error}</p>
            <GhostBtn onClick={load}>Try Again</GhostBtn>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="📥" title="No inquiries found" sub="Submissions from the website's Corporate Inquiry form will show up here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                  <Th>Name</Th><Th>Company</Th><Th>Type</Th>
                  <Th>Date</Th><Th>Status</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i, idx) => (
                  <Tr key={i.id} last={idx === filtered.length - 1}>
                    <Td>
                      <div className="flex items-center gap-2.5">
                        <Avatar name={`${i.first_name} ${i.last_name}`} size={32} />
                        <div>
                          <p className="text-[12px] font-semibold" style={{ color:T.textPri }}>{i.first_name} {i.last_name}</p>
                          <p className="text-[10px]" style={{ color:T.textSec }}>{i.email}</p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <p className="text-[12px] font-medium" style={{ color:T.textPri }}>{i.company || "—"}</p>
                      <p className="text-[10px]" style={{ color:T.textSec }}>{i.company_size ? `${i.company_size} employees` : ""}</p>
                    </Td>
                    <Td><span className="text-[11px]" style={{ color:T.textSec }}>{i.inquiry_type || "—"}</span></Td>
                    <Td><span className="text-[11px]" style={{ color:T.textSec }}>{fmtDate(i.created_at)}</span></Td>
                    <Td><StatusPill s={i.status} /></Td>
                    <Td>
                      <div className="flex gap-1">
                        <button onClick={() => setDetail(i)}
                          className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                          style={{ borderColor:T.border, color:T.textSec }}>View</button>
                        <button onClick={() => setDelId(i.id)}
                          className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-red-50"
                          style={{ borderColor:T.border, color:T.danger }}>🗑</button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
          <div className="w-full h-full sm:h-auto sm:max-w-lg rounded-none sm:rounded-3xl overflow-hidden sm:max-h-[90vh] flex flex-col" style={{ background:T.card }}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderBottom:`1px solid ${T.border}` }}>
              <div className="min-w-0">
                <h2 className="text-[16px] sm:text-[17px] font-bold truncate" style={{ color:T.textPri, fontFamily:T.serif }}>
                  {detail.first_name} {detail.last_name}
                </h2>
                <p className="text-[11px]" style={{ color:T.textSec }}>{fmtDate(detail.created_at)}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 rounded-2xl p-4" style={{ background:T.bgSoft }}>
                {[
                  ["Email", detail.email], ["Company", detail.company || "—"],
                  ["Company Size", detail.company_size || "—"], ["Inquiry Type", detail.inquiry_type || "—"],
                  ["Source", detail.source || "—"], ["Status", detail.status],
                ].map(([l,v]) => (
                  <div key={l} className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color:T.textSec }}>{l}</p>
                    <p className="text-[13px] font-medium break-words" style={{ color:T.textPri }}>{v}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color:T.textSec }}>Message</p>
                <p className="text-[13px] leading-relaxed rounded-2xl p-4" style={{ background:T.bgSoft, color:T.textPri }}>
                  {detail.message}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap pt-2">
                <div className="flex flex-wrap gap-2">
                  {["New","Contacted","Resolved"].filter(s => s !== detail.status).map(s => (
                    <button key={s} onClick={() => updateStatus(detail.id, s)}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:bg-amber-50"
                      style={{ borderColor:T.gold, color:T.gold }}>
                      Mark as {s}
                    </button>
                  ))}
                </div>
                <a href={`mailto:${detail.email}`}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white text-center sm:ml-auto"
                  style={{ background:T.gold }}>
                  📧 Reply by Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {delId && (
        <ConfirmModal
          title="Delete Inquiry?"
          message="This inquiry will be permanently deleted."
          onConfirm={() => handleDelete(delId)}
          onCancel={() => setDelId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[100] px-5 py-3 rounded-2xl text-white text-[13px] font-semibold shadow-2xl text-center sm:text-left"
          style={{ background: toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}